import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TabsActions } from '@material-ui/core/Tabs';

import {
  clamp,
  INDICATOR_UPDATE_DELAY,
  SCROLLER_SELECTOR,
  SCROLL_POSITION_EPSILON,
  smoothScrollTo,
  TAB_SELECTION_DELAY,
  TAB_SELECTOR,
} from './Tabs.helpers';

export type TabChangeHandler = (event: React.ChangeEvent<{}>, newValue: number) => void;

export const useTabs = (initialTabValue = 0) => {
  const [tabValue, setTabValue] = useState(initialTabValue);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  return { tabValue, handleTabChange };
};

export const useTabsScroll = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getTabElements = useCallback(
    () => Array.from(wrapperRef.current?.querySelectorAll<HTMLElement>(TAB_SELECTOR) ?? []),
    [],
  );

  const scrollTabIntoCenter = useCallback((tabIndex: number) => {
    const scroller = wrapperRef.current?.querySelector<HTMLDivElement>(SCROLLER_SELECTOR) ?? null;
    const tabs = getTabElements();
    const tab = tabs[tabIndex];

    if (!scroller || !tab) return;

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

    if (tabIndex === 0) {
      smoothScrollTo(scroller, 0);
      return;
    }

    if (tabIndex === tabs.length - 1) {
      smoothScrollTo(scroller, maxScrollLeft);
      return;
    }

    const centeredScrollLeft = tab.offsetLeft + tab.offsetWidth / 2 - scroller.clientWidth / 2;
    const nextScrollLeft = clamp(centeredScrollLeft, 0, maxScrollLeft);

    smoothScrollTo(scroller, nextScrollLeft);
  }, [getTabElements]);

  const updateScrollState = useCallback(() => {
    const scroller = wrapperRef.current?.querySelector<HTMLDivElement>(SCROLLER_SELECTOR) ?? null;
    if (!scroller) return;

    setCanScrollLeft(scroller.scrollLeft > SCROLL_POSITION_EPSILON);
    setCanScrollRight(
      scroller.scrollLeft
        < scroller.scrollWidth - scroller.clientWidth - SCROLL_POSITION_EPSILON,
    );
  }, []);

  useEffect(() => {
    const scroller = wrapperRef.current?.querySelector<HTMLDivElement>(SCROLLER_SELECTOR) ?? null;
    if (!scroller) return;

    updateScrollState();
    scroller.addEventListener('scroll', updateScrollState, { passive: true });

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(scroller);

    return () => {
      scroller.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const handleScrollRight = useCallback(() => {
    const scroller = wrapperRef.current?.querySelector<HTMLDivElement>(SCROLLER_SELECTOR) ?? null;
    if (!scroller) return;

    const tabs = getTabElements();
    const visibleRight = scroller.scrollLeft + scroller.clientWidth;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    const nextTab = tabs.find(
      tab => tab.offsetLeft + tab.offsetWidth > visibleRight - SCROLL_POSITION_EPSILON,
    );

    if (nextTab) {
      const nextScrollLeft = Math.min(scroller.scrollLeft + nextTab.offsetWidth, maxScrollLeft);
      smoothScrollTo(scroller, nextScrollLeft);
    }
  }, [getTabElements]);

  const handleScrollLeft = useCallback(() => {
    const scroller = wrapperRef.current?.querySelector<HTMLDivElement>(SCROLLER_SELECTOR) ?? null;
    if (!scroller) return;

    const tabs = getTabElements();
    const currentScrollLeft = scroller.scrollLeft;
    const prevTab = [...tabs]
      .reverse()
      .find(tab => tab.offsetLeft < currentScrollLeft - SCROLL_POSITION_EPSILON);

    smoothScrollTo(scroller, prevTab?.offsetLeft ?? 0);
  }, [getTabElements]);

  return {
    wrapperRef,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
    scrollTabIntoCenter,
  };
};

export const useDeferredTabValue = (
  value: number,
  handleChange: TabChangeHandler,
  scrollTabIntoCenter: (tabIndex: number) => void,
) => {
  const [displayedValue, setDisplayedValue] = useState(value);
  const latestValueRef = useRef(value);
  const pendingValueRef = useRef<number | null>(null);
  const selectionTimerRef = useRef<ReturnType<typeof setTimeout>>();
  latestValueRef.current = value;

  const handleDeferredChange = useCallback<TabChangeHandler>(
    (event, newValue) => {
      clearTimeout(selectionTimerRef.current);
      pendingValueRef.current = newValue;
      scrollTabIntoCenter(newValue);
      event.persist?.();

      // MUI scrolls a newly selected, partially hidden tab for 300 ms.
      // Delay both the selected tab value and panel value update
      // until our centering animation is complete.
      selectionTimerRef.current = setTimeout(() => {
        setDisplayedValue(newValue);
        pendingValueRef.current = null;
        handleChange(event, newValue);
      }, TAB_SELECTION_DELAY);
    },
    [handleChange, scrollTabIntoCenter],
  );

  useEffect(() => {
    if (pendingValueRef.current === value) return;

    clearTimeout(selectionTimerRef.current);
    pendingValueRef.current = null;
    setDisplayedValue(value);
  }, [value]);

  useEffect(() => () => clearTimeout(selectionTimerRef.current), []);

  return { displayedValue, handleDeferredChange };
};

  // Fix - https://github.com/mui-org/material-ui/issues/20527
export const useTabsIndicator = (tabsCount: number) => {
  const tabsActions = useRef<TabsActions | null>(null);

  useEffect(() => {
    if (!tabsActions.current) return;

    const timer = setTimeout(
      () => tabsActions.current?.updateIndicator(),
      INDICATOR_UPDATE_DELAY,
    );

    return () => clearTimeout(timer);
  }, [tabsCount]);

  return tabsActions;
};
